import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs } from "../../../../ui/components/Tabs";
import { ApprovalSidebar } from "../components/ApprovalSidebar";
import { ApprovalList } from "../components/ApprovalList";
import { ApprovalDetailPanel } from "../components/ApprovalDetailPanel";
import { ApprovalDetailViewPanel } from "../components/ApprovalDetailViewPanel";
import { ApprovalViewCard } from "../components/ApprovalViewCard";
import { useApprovals } from "../hooks/useApprovals";
import { useMyRequests } from "../hooks/useMyRequests";
import { useApprovalCounts } from "../hooks/useApprovalCounts";
import { ApproveReviewUseCase, RejectReviewUseCase } from "../../domain";
import { ApprovalRepositoryImpl } from "../../data/repositories/ApprovalRepositoryImpl";
import { useToast } from "../../../../shared/presentation/components";
import { useErrorHandling } from "../../../../shared/presentation/hooks";
import { ApprovalEntity } from "../../domain/entities/ApprovalEntity";
import {
  approveMonthEndChecklistReview,
  rejectMonthEndChecklistReview,
} from "../../data/api/approvalsApi";

// Create repository and use cases
const approvalRepository = new ApprovalRepositoryImpl();
const approveReviewUseCase = new ApproveReviewUseCase(approvalRepository);
const rejectReviewUseCase = new RejectReviewUseCase(approvalRepository);

export const ApprovalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params on mount
  const getInitialEntityType = (): string | null => {
    const entityType = searchParams.get("entity_type");
    return entityType || "invoice";
  };

  const getInitialStatus = (): "pending" | "approved" | "rejected" | null => {
    const statusParam = searchParams.get("status");
    if (
      statusParam === "pending" ||
      statusParam === "approved" ||
      statusParam === "rejected"
    ) {
      return statusParam;
    }
    return "pending";
  };

  const getInitialSearch = (): string => {
    // Use search param or review_id as search term
    const searchParam = searchParams.get("search");
    const reviewId = searchParams.get("review_id");
    // If review_id is present but no search, use review_id as search term
    return searchParam || reviewId || "";
  };

  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(
    getInitialEntityType(),
  );
  const [status, setStatus] = useState<
    "pending" | "approved" | "rejected" | null
  >(getInitialStatus());
  const [activeTab, setActiveTab] = useState<
    "pending" | "approved" | "rejected" | "my-requests"
  >("pending");
  const [selectedApproval, setSelectedApproval] =
    useState<ApprovalEntity | null>(null);
  const [search, setSearch] = useState(getInitialSearch());
  const [offset, setOffset] = useState(0);
  const [myRequestsOffset, setMyRequestsOffset] = useState(0);
  const limit = 20;

  const { showSuccess } = useToast();
  const { setApiErrors } = useErrorHandling();
  const { counts, myRequestsCounts, myRequestsCount, fetchCounts } =
    useApprovalCounts();
  const {
    approvals,
    pagination,
    loading: approvalsLoading,
    fetchApprovals,
    refresh: refreshApprovals,
  } = useApprovals();
  const {
    myRequests,
    pagination: myRequestsPagination,
    loading: myRequestsLoading,
    fetchMyRequests,
  } = useMyRequests();

  // Update URL params when state changes (only on user actions, not on mount)
  const updateParams = useCallback(
    (updates: {
      entityType?: string | null;
      status?: "pending" | "approved" | "rejected" | null;
      search?: string | undefined;
      reviewId?: string | null;
    }) => {
      const newParams = new URLSearchParams(searchParams);

      if (updates.entityType !== undefined) {
        if (updates.entityType) {
          newParams.set("entity_type", updates.entityType);
        } else {
          newParams.delete("entity_type");
        }
      }

      if (updates.status !== undefined) {
        if (updates.status) {
          newParams.set("status", updates.status);
        } else {
          newParams.delete("status");
        }
      }

      if (updates.search !== undefined) {
        if (updates.search) {
          newParams.set("search", updates.search);
        } else {
          newParams.delete("search");
        }
        // Remove review_id when search changes
        newParams.delete("review_id");
      }

      if (updates.reviewId !== undefined) {
        if (updates.reviewId) {
          newParams.set("review_id", updates.reviewId);
        } else {
          newParams.delete("review_id");
        }
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Fetch counts on mount
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Fetch approvals when filters change (not for my-requests on mount)
  useEffect(() => {
    if (selectedEntityType && activeTab !== "my-requests") {
      const params = {
        entityType: selectedEntityType,
        status: status || undefined,
        limit,
        offset: 0,
        search: search || undefined,
      };
      fetchApprovals(params);
      setOffset(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityType, status, search]);

  // Fetch my requests only when on my-requests tab
  useEffect(() => {
    if (selectedEntityType && activeTab === "my-requests") {
      const params = {
        entityType: selectedEntityType,
        limit,
        offset: 0,
        search: search || undefined,
      };
      fetchMyRequests(params);
      setMyRequestsOffset(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityType, search, activeTab]);

  // Handle deep link review ID on mount and when approvals load
  useEffect(() => {
    const reviewId = searchParams.get("review_id");
    if (reviewId && approvals.length > 0) {
      // Use review_id as search term to find the review
      const review = approvals.find((a) => String(a.id) === reviewId);
      if (review) {
        setSelectedApproval(review);
        // Scroll to review after a short delay
        setTimeout(() => {
          const element = document.getElementById(`approval-${reviewId}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            // Highlight the element temporarily
            element.style.backgroundColor = "#fff3cd";
            setTimeout(() => {
              element.style.backgroundColor = "";
            }, 3000);
          }
        }, 500);
      }
    }
  }, [searchParams, approvals]);

  const handleEntityTypeChange = useCallback(
    (entityType: string) => {
      setSelectedEntityType(entityType);
      setSelectedApproval(null);
      setOffset(0);
      updateParams({ entityType });
    },
    [updateParams],
  );

  const handleStatusChange = useCallback(
    (newStatus: "pending" | "approved" | "rejected" | null) => {
      setStatus(newStatus);
      setSelectedApproval(null);
      setOffset(0);
      updateParams({ status: newStatus });
    },
    [updateParams],
  );

  const handleTabChange = useCallback(
    (tab: "pending" | "approved" | "rejected" | "my-requests") => {
      setActiveTab(tab);
      setSelectedApproval(null);
      setOffset(0);
      setMyRequestsOffset(0);

      if (tab === "my-requests") {
        // For my requests, we don't use status
        setStatus(null);
        // Default to invoice if no entity type selected
        if (!selectedEntityType) {
          setSelectedEntityType("invoice");
        }
      } else {
        // For other tabs, set the corresponding status
        setStatus(tab);
        updateParams({ status: tab });
      }
    },
    [updateParams, selectedEntityType],
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearch(searchValue);
      setOffset(0);
      updateParams({ search: searchValue || undefined });
    },
    [updateParams],
  );

  const handleReview = useCallback((approval: ApprovalEntity) => {
    setSelectedApproval(approval);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedApproval(null);
  }, []);

  const handleApprove = useCallback(
    async (reviewId: number, comments?: string) => {
      try {
        // Use the correct API based on entity type
        if (selectedApproval?.entityType === "month_end_checklist_item") {
          await approveMonthEndChecklistReview(reviewId, comments);
        } else {
          await approveReviewUseCase.execute(reviewId, comments);
        }
        showSuccess("Review approved successfully");
        setSelectedApproval(null);
        await refreshApprovals();
        await fetchCounts();
      } catch (error: any) {
        setApiErrors(error);
        throw error;
      }
    },
    [
      showSuccess,
      setApiErrors,
      refreshApprovals,
      fetchCounts,
      selectedApproval,
    ],
  );

  const handleReject = useCallback(
    async (reviewId: number, comments: string) => {
      try {
        // Use the correct API based on entity type
        if (selectedApproval?.entityType === "month_end_checklist_item") {
          await rejectMonthEndChecklistReview(reviewId, comments);
        } else {
          await rejectReviewUseCase.execute(reviewId, comments);
        }
        showSuccess("Review rejected successfully");
        setSelectedApproval(null);
        await refreshApprovals();
        await fetchCounts();
      } catch (error: any) {
        setApiErrors(error);
        throw error;
      }
    },
    [
      showSuccess,
      setApiErrors,
      refreshApprovals,
      fetchCounts,
      selectedApproval,
    ],
  );

  const handleLoadMore = useCallback(() => {
    if (activeTab === "my-requests") {
      if (myRequestsPagination?.hasMore && selectedEntityType) {
        const newOffset = myRequestsOffset + limit;
        setMyRequestsOffset(newOffset);
        fetchMyRequests({
          entityType: selectedEntityType,
          limit,
          offset: newOffset,
          search: search || undefined,
        });
      }
    } else {
      if (pagination?.hasMore && selectedEntityType) {
        const newOffset = offset + limit;
        setOffset(newOffset);
        fetchApprovals({
          entityType: selectedEntityType,
          status: status || undefined,
          limit,
          offset: newOffset,
          search: search || undefined,
        });
      }
    }
  }, [
    activeTab,
    pagination,
    myRequestsPagination,
    offset,
    myRequestsOffset,
    limit,
    selectedEntityType,
    status,
    search,
    fetchApprovals,
    fetchMyRequests,
  ]);

  return (
    <div className="flex h-full w-full flex-col items-start bg-default-background">
      {/* Header Section */}
      <div className="flex w-full flex-col items-start gap-3 border-b border-solid border-neutral-border bg-default-background px-8 py-4">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Approvals
            </span>
            <span className="text-body font-body text-subtext-color">
              Review and approve accounting items before finalization
            </span>
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <Tabs>
            <Tabs.Item
              active={activeTab === "pending"}
              onClick={() => handleTabChange("pending")}
            >
              Pending (
              {counts
                ? Object.values(counts).reduce(
                    (sum, entity) => sum + entity.pending,
                    0,
                  )
                : 0}
              )
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "approved"}
              onClick={() => handleTabChange("approved")}
            >
              Approved (
              {counts
                ? Object.values(counts).reduce(
                    (sum, entity) => sum + entity.approved,
                    0,
                  )
                : 0}
              )
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "rejected"}
              onClick={() => handleTabChange("rejected")}
            >
              Sent Back (
              {counts
                ? Object.values(counts).reduce(
                    (sum, entity) => sum + entity.rejected,
                    0,
                  )
                : 0}
              )
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "my-requests"}
              onClick={() => handleTabChange("my-requests")}
            >
              My Requests ({myRequestsCount || 0})
            </Tabs.Item>
          </Tabs>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="flex w-full grow shrink-0 basis-0 items-start overflow-hidden">
        <ApprovalSidebar
          counts={activeTab === "my-requests" ? myRequestsCounts : counts}
          selectedEntityType={selectedEntityType}
          onEntityTypeChange={handleEntityTypeChange}
          status={activeTab === "my-requests" ? null : status}
        />
        {activeTab === "my-requests" ? (
          <ApprovalList
            approvals={myRequests}
            pagination={myRequestsPagination}
            loading={myRequestsLoading}
            selectedEntityType={selectedEntityType}
            searchQuery={search}
            status={null}
            onStatusChange={handleStatusChange}
            onSearch={handleSearch}
            onReview={handleReview}
            onLoadMore={handleLoadMore}
            isMyRequestsView={true}
            renderCustomCard={(approval: ApprovalEntity) => (
              <ApprovalViewCard
                key={approval.id}
                approval={approval}
                onView={() => handleReview(approval)}
              />
            )}
          />
        ) : (
          <ApprovalList
            approvals={approvals}
            pagination={pagination}
            loading={approvalsLoading}
            selectedEntityType={selectedEntityType}
            searchQuery={search}
            status={status}
            onStatusChange={handleStatusChange}
            onSearch={handleSearch}
            onReview={handleReview}
            onLoadMore={handleLoadMore}
          />
        )}
        {selectedApproval &&
          (activeTab === "my-requests" ? (
            <ApprovalDetailViewPanel
              approval={selectedApproval}
              onClose={handleCloseDetail}
            />
          ) : (
            <ApprovalDetailPanel
              approval={selectedApproval}
              onClose={handleCloseDetail}
              onApprove={handleApprove}
              onReject={handleReject}
              loading={approvalsLoading}
            />
          ))}
      </div>
    </div>
  );
};
